import React from 'react';

import { TextComponent } from 'design/text/textComponents';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { FormControlComponent } from 'design/formControl';
import { useAsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryHooks';
import { useServices } from 'hooks/useServices';
import { PressControlComponent } from 'design/pressControl';
import { PartnerProfileFieldset } from 'containers/PartnerProfileFieldset';
import { validatePartnerProfileFormValues } from 'utils/validatePartnerProfileFormValues';
import { usePartnerProfileForm } from 'hooks/usePartnerProfileForm';
import { useUser } from 'hooks/useUser';
import { normalizeUserProfileInfo } from 'services/production/utils/normalizeUserProfileInfo';
import { ContentLayoutComponent } from 'design/contentLayout';
import { MarginComponent } from 'design/margin';
import { MaximumAllowedFileSizeExceededError } from 'utils/errors';

type Props = {
  onSubmitDidSucceed(): void;
};

export const PartnerRegistrationManagerProfileForm = (props: Props) => {
  const { onSubmitDidSucceed } = props;

  const { submitPartnerProfileForm } = useServices();

  const intl = useIntlDictionary();

  const user = useUser();

  const {
    partnerRegistrationManager: { profileForm: ownDictionary },
  } = intl;

  const {
    values: formValues,
    violations: formViolations,
    setValues: setFormValues,
    setViolations: setFormViolations,
    setFirstName,
    setLastName,
    setMiddleName,
    setPhone,
    setCountry,
    setCity,
    setAvatar,
    setDetails,
  } = usePartnerProfileForm();

  React.useLayoutEffect(() => {
    if (user?.profileInfo) {
      setFormValues(
        normalizeUserProfileInfo(user?.profileInfo).toPartnerProfileFormValues()
      );
    }
  }, [setFormValues, user?.profileInfo]);

  const { active: asyncActive, injectPromise } = useAsyncBoundary();

  const submitForm = React.useCallback(() => {
    return submitPartnerProfileForm(formValues)
      .then(onSubmitDidSucceed)
      .catch((error) => {
        if (error instanceof MaximumAllowedFileSizeExceededError) {
          setFormViolations((prev) => [
            ...prev,
            { name: 'AvatarCroppedFileSize' },
          ]);
        } else {
          throw error;
        }
      });
  }, [
    formValues,
    onSubmitDidSucceed,
    setFormViolations,
    submitPartnerProfileForm,
  ]);

  const submit = React.useCallback(() => {
    if (formViolations.length === 0) {
      const nextViolations = validatePartnerProfileFormValues(
        formValues,
        false
      );

      if (nextViolations.length === 0) {
        const promise = submitForm();

        injectPromise(promise, { shouldShowFallback: false });
      } else {
        Promise.resolve(nextViolations).then(setFormViolations);
      }
    }
  }, [
    formValues,
    formViolations.length,
    injectPromise,
    setFormViolations,
    submitForm,
  ]);

  const hasViolatedField = formViolations.length > 0;

  return (
    <FormControlComponent
      onSubmit={submit}
      shouldFocusFirstInvalidInput={hasViolatedField}
    >
      <ContentLayoutComponent
        title={
          <TextComponent color="primary" size="l" family="futura">
            {ownDictionary.titleText}
          </TextComponent>
        }
        body={
          <>
            <MarginComponent top={28} />
            <PartnerProfileFieldset
              disabled={asyncActive}
              values={formValues}
              violations={formViolations}
              onFirstNameChange={setFirstName}
              onLastNameChange={setLastName}
              onMiddleNameChange={setMiddleName}
              onPhoneChange={setPhone}
              onCountryChange={setCountry}
              onCityChange={setCity}
              onAvatarChange={setAvatar}
              onDetailsChange={setDetails}
            />
            <MarginComponent top={48} />
            <span>
              <PressControlComponent
                disabled={asyncActive}
                type="submit"
                theme="primary"
                hasSpinner={asyncActive}
              >
                {ownDictionary.submitActionText}
              </PressControlComponent>
            </span>
          </>
        }
        backAction={null}
      />
    </FormControlComponent>
  );
};
